import { Box, Button, Chip, Divider, Popover, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { memo, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PopoverIcon } from './PopoverIcon';
import {
  NAVBAR_LINK_SERVICES,
  NAVBAR_SERVICES_HEADING,
  NAVBAR_SERVICES_SUBHEADING,
} from '../../../locales/types';
import { services } from './NavbarNavigateOptions';

/**
 * Desktop mega-menu — modern 3-column grid with per-service accent colors,
 * gradient header, and smooth hover transitions.
 */
export const ServicesPopover = memo(({ afterSelect, ...props }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimer = useRef(null);
  const isOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const handleOpen = useCallback(
    (e) => {
      cancelClose();
      setAnchorEl(e.currentTarget);
    },
    [cancelClose],
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(handleClose, 150);
  }, [cancelClose, handleClose]);

  const handleSelect = useCallback(
    (item) => {
      navigate(item.href);
      afterSelect?.(item);
      handleClose();
    },
    [navigate, afterSelect, handleClose],
  );

  return (
    <Box {...props} onMouseLeave={scheduleClose} onMouseEnter={cancelClose}>
      <Button
        onMouseEnter={handleOpen}
        onClick={(e) => (isOpen ? handleClose() : handleOpen(e))}
        aria-expanded={isOpen}
        aria-haspopup="true"
        endIcon={<PopoverIcon isOpen={isOpen} />}
        disableRipple
        sx={{
          color: 'common.white',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.95rem',
          px: 1.25,
          py: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: alpha('#fff', 0.08) },
        }}
      >
        <Trans i18nKey={`component.navbar.${NAVBAR_LINK_SERVICES}`} />
      </Button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            elevation: 0,
            sx: {
              mt: 1.5,
              width: { xs: 'calc(100vw - 32px)', md: 720 },
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: `0 24px 60px ${alpha('#000', 0.18)}, 0 0 0 1px ${alpha(primary, 0.12)}`,
              border: `1px solid ${alpha(primary, 0.1)}`,
            },
          },
        }}
      >
        {/* Gradient header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            background: `linear-gradient(135deg, ${alpha(primary, 0.08)} 0%, ${alpha(secondary, 0.06)} 100%)`,
            borderBottom: `1px solid ${alpha(primary, 0.1)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack spacing={0.25}>
            <Typography sx={{ fontWeight: 800, fontSize: 13.5, letterSpacing: 0.4, textTransform: 'uppercase', color: primary }}>
              <Trans i18nKey={`component.navbar.${NAVBAR_SERVICES_HEADING}`} />
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12.5 }}>
              <Trans i18nKey={`component.navbar.${NAVBAR_SERVICES_SUBHEADING}`} />
            </Typography>
          </Stack>
          <Chip
            label="Bilingual"
            size="small"
            sx={{ bgcolor: alpha(primary, 0.1), color: primary, fontWeight: 700, fontSize: 11, border: `1px solid ${alpha(primary, 0.2)}` }}
          />
        </Box>

        {/* 3-column service grid */}
        <Box
          sx={{
            p: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 1,
          }}
        >
          {services.map((item) => {
            const Icon = item.icon;
            const accent = item.accent === 'secondary' ? secondary : primary;
            return (
              <Box
                key={item.title}
                component="button"
                type="button"
                onClick={() => handleSelect(item)}
                sx={{
                  textAlign: 'left',
                  background: 'transparent',
                  border: `1px solid transparent`,
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  p: 1.5,
                  borderRadius: 2.5,
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    bgcolor: alpha(accent, 0.06),
                    borderColor: alpha(accent, 0.25),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 18px ${alpha(accent, 0.14)}`,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${accent}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(accent, 0.1),
                    color: accent,
                    border: `1px solid ${alpha(accent, 0.2)}`,
                    transition: 'background-color 0.18s ease',
                    'button:hover &': {
                      bgcolor: alpha(accent, 0.18),
                    },
                  }}
                >
                  {Icon && <Icon size={20} />}
                </Box>
                <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: 'text.primary', lineHeight: 1.3 }}>
                    <Trans i18nKey={`component.navbar.${item.trans}`} />
                  </Typography>
                  {item.description && (
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.4, fontSize: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      <Trans i18nKey={`component.navbar.${item.description}`} />
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            px: 3,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(primary, 0.02),
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11.5 }}>
            6 services · Miami Lakes, FL
          </Typography>
          <Button
            size="small"
            onClick={() => { navigate('/'); handleClose(); }}
            sx={{ fontSize: 11.5, fontWeight: 700, color: primary, textTransform: 'none', py: 0.25, minWidth: 0 }}
          >
            View all →
          </Button>
        </Box>
      </Popover>
    </Box>
  );
});

ServicesPopover.propTypes = {
  afterSelect: PropTypes.func,
};

ServicesPopover.displayName = 'ServicesPopover';


ServicesPopover.propTypes = {
  afterSelect: PropTypes.func,
};

ServicesPopover.displayName = 'ServicesPopover';
